// report-effect.js
import { of } from "rxjs";
import { ajax } from "rxjs/ajax";
import { catchError, filter, mergeMap } from "rxjs/operators";
import { ofType } from "redux-observable";
import { push } from "connected-react-router";
import { authHeaders } from "frame/auth-headers";
import { tokenExists } from "frame/access-token";
import { getOrganisationBaseUrl } from "frame";
import fromTypes from "../actions/types";
import ReactGA from "react-ga";
import { isProd } from "utils";
import { reportDownloadSuccess, reportDownloadFailed, openTimesheetReportSuccess, openTimesheetReportFailed, openLeaveReportSuccess, timesheetReportFetchSuccess, timesheetReportFetchFailed, openEquipmentUsageReportSuccess } from "../actions";
import moment from "moment";

export const reportDownloadEffect = (action$, state$) =>
	action$.pipe(
		ofType(fromTypes.REPORT_DOWNLOAD),
		filter(() => tokenExists()),
		mergeMap(({ payload }) => {
			const { report, staffId, siteId } = payload;
			const id = staffId || siteId || 0;
			const extra = report === "timesheets" ? "&view=configured" : "";
			const url = `${getOrganisationBaseUrl(state$.value)}/${requestUrl(
				report
			)}/export${requestDateRange(payload)}${requestId(
				report,
				id
			)}${extra}`;

			return ajax({
				url,
				method: "GET",
				headers: authHeaders(),
				// Ensure CSV/text responses are not treated as JSON.
				responseType: "text",
			}).pipe(
				mergeMap((ajaxResponse) => {
					isProd &&
						ReactGA.event({
							category: "My Account",
							action: "Download Clicked",
							label: `Report ${report}`,
						});
					return of(exportCSVFile(ajaxResponse.response, report));
				}),
				catchError((error) =>
					of(reportDownloadFailed(error.response?.error || error.message))
				)
			);
		})
	);

const requestId = (report, id) => {
	switch (report) {
		case "staff":
			return `&staffId=${id}`;
		case "hazards":
		case "hazardous_substances":
			return `&siteId=${id}`;
		case "timesheets":
			return `&siteId=${id}`;
		case "equipment_usage":
			return id && id !== "all" && id !== 0 ? `&siteId=${id}` : "";
		default:
			return "";
	}
};

const requestUrl = (value) => {
	switch (value) {
		case "accidents":
		case "timesheets":
		case "hazards":
			return value.slice(0, -1); // take out 's'
		case "leaves":
			return "timesheet/leaves"; // Special case for leaves
		case "inductions":
			return "induction-minutes";

		case "safety_minutes":
			return "safety-minutes";
		case "hazardous_substances":
			return "hazardous-substance";
		case "staff_training":
			return "staff-training";
		case "equipment_usage":
			return "equipment-usage";
		default:
			return value;
	}
};

const requestDateRange = (payload) => {
	let defaultFrom = moment().subtract(10, "years").utc().format();
	let defaultTo = moment().utc().format();

	return payload.from !== undefined && payload.to !== undefined ?
		`?from=${moment.utc(payload.from).format()}&to=${moment
			.utc(payload.to)
			.format()}` :
		`?from=${defaultFrom}&to=${defaultTo}`;
};

const exportCSVFile = (csv, fileTitle) => {
	let exportedFilenmae = fileTitle + ".csv" || "export.csv";

	let blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
	if (navigator.msSaveBlob) {
		// IE 10+
		navigator.msSaveBlob(blob, exportedFilenmae);
	} else {
		// force download link
		let link = document.createElement("a");
		if (link.download !== undefined) {
			// feature detection
			// Browsers that support HTML5 download attribute
			let url = URL.createObjectURL(blob);
			link.setAttribute("href", url);
			link.setAttribute("download", exportedFilenmae);
			link.style.visibility = "hidden";
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		}
	}

	return reportDownloadSuccess();
};

export const openTimesheetReportEffect = (action$, state$) =>
	action$.pipe(
		ofType(fromTypes.OPEN_TIMESHEET_REPORT),
		filter(() => tokenExists()),
		mergeMap(({ payload }) => {
			const { report, staffId, siteId, from, to } = payload;
			const id = siteId || 0;
			// Build query parameters for the timesheet-report page
			const queryParams = new URLSearchParams();
			if (from) queryParams.set('from', moment.utc(from).format());
			if (to) queryParams.set('to', moment.utc(to).format());
			if (siteId && siteId !== 'all') queryParams.set('siteId', siteId);
			if (staffId) queryParams.set('staffId', staffId);
			
			// Navigate with query parameters instead of location.state
			const url = `/timesheet-report?${queryParams.toString()}`;
			return of(push(url), openTimesheetReportSuccess());
		})
	);

export const openEquipmentUsageReportEffect = (action$, state$) =>
	action$.pipe(
		ofType(fromTypes.OPEN_EQUIPMENT_USAGE_REPORT),
		filter(() => tokenExists()),
		mergeMap(({ payload }) => {
			const { siteId, from, to } = payload;
			const queryParams = new URLSearchParams();
			if (from) queryParams.set("from", moment.utc(from).format());
			if (to) queryParams.set("to", moment.utc(to).format());
			if (siteId && siteId !== "all") queryParams.set("siteId", siteId);
			const url = `/equipment-usage-report?${queryParams.toString()}`;
			return of(push(url), openEquipmentUsageReportSuccess());
		})
	);

// New effect to fetch timesheet report data based on filters.
// Use responseType: 'text' so CSV body is received as string (default 'json' would fail on text/csv).
export const timesheetReportFetchEffect = (action$, state$) =>
	action$.pipe(
		ofType(fromTypes.TIMESHEET_REPORT_FETCH),
		filter(() => tokenExists()),
		mergeMap(({ payload }) => {
			const { from, to, siteId, staffId, approvedOnly } = payload;
			const id = siteId && siteId !== 'all' ? siteId : 0;
			const approvedOnlyParam = approvedOnly ? '&approvedOnly=true' : '';
			const url = `${getOrganisationBaseUrl(state$.value)}/timesheet/export${requestDateRange(payload)}${id ? `&siteId=${id}` : ''}&view=full${approvedOnlyParam}`;
			return ajax({
				url,
				method: "GET",
				headers: authHeaders(),
				responseType: "text"
			}).pipe(
				mergeMap((ajaxResponse) => {
					return of(timesheetReportFetchSuccess(ajaxResponse.response));
				}),
				catchError((error) =>
					of(timesheetReportFetchFailed(error.response?.error || error.message))
				)
			);
		})
	);

export const openLeaveReportEffect = (action$, state$) =>
	action$.pipe(
		ofType(fromTypes.OPEN_LEAVE_REPORT),
		filter(() => tokenExists()),
		mergeMap(({ payload }) => {
			const { report, staffId, siteId } = payload;
			const id = staffId || siteId || 0;
			return ajax
				.get(
					`${getOrganisationBaseUrl(state$.value)}/${requestUrl(
						report
					)}/export${requestDateRange(payload)}${requestId(
						report,
						id
					)}`,
					authHeaders()
				)
				.pipe(
					mergeMap((ajaxResponse) => {
						isProd &&
							ReactGA.event({
								category: "My Account",
								action: "Download Clicked",
								label: `Report ${report}`,
							});
						const from = payload.from ? moment.utc(payload.from).format() : moment().subtract(7, "days").startOf("day").utc().format();
						const to = payload.to ? moment.utc(payload.to).format() : moment().startOf("day").utc().format();
						return of(
							push(`/timesheet-report?type=leave&from=${from}&to=${to}`, ajaxResponse.response),
							openLeaveReportSuccess(ajaxResponse.response)
						);
					}),
					catchError((error) =>
						of(reportDownloadFailed(error.response?.error))
					)
				);
		})
	);